import { useNode } from "@craftjs/core";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import WheelInfoCard from "./WheelInfoCard";

import _ from "lodash";
import BrowseWheels from "./BrowseWheels";
import {
  getLoggedInUser,
  opacityToSlider,
  sliderToOpacity,
} from "../../../../../utils/helpers";
import TextView from "../../../../TextView";
import Tabs from "../../../../Tabs";
import {
  getQueriedUserWheels,
  getWheelDetails,
} from "../../../../../services/api";
import CustomSwitch from "../../../../CustomSwitch";
import SettingsSlider from "../../../Shared/SettingsSlider";
import ColorPopup from "../../../Shared/ColorPopup";
import EmbedURL from "../../../Shared/EmbedURL";
import MarginBox from "../../../Shared/MarginBox";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { updateLinkWheelStyles } from "../../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { toggleSegmentSettings } from "../../../../../redux/actions/segmentSettings";

const LinkWheelSettings = () => {
  const {
    actions: { setProp },
    wheelId,
    selectedWheelData,
    showDescription,
    borderRadius,
    titleColor,
    textColor,
    borderColor,
    borderWidth,
    dropShadowColor,
    backgroundColor,
    backgroundOpacity,
    margin,
    isApplyMarginOnAllSides,
    allSideMargin,
  } = useNode((node) => ({
    wheelId: node.data.props.wheelId,
    showDescription: node.data.props.showDescription,
    borderRadius: node.data.props.borderRadius,
    titleColor: node.data.props.titleColor,
    textColor: node.data.props.textColor,
    borderColor: node.data.props.borderColor,
    borderWidth: node.data.props.borderWidth,
    dropShadowColor: node.data.props.dropShadowColor,
    backgroundColor: node.data.props.backgroundColor,
    backgroundOpacity: node.data.props.backgroundOpacity,
    margin: node.data.props.margin,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    allSideMargin: node.data.props.allSideMargin,
  }));

  const mainTabs = ["Wheel Selection", "Layout"];
  const categories = ["Browse", "Embed URL"];
  const wheelDropdownOptions = ["All", "Public", "Private", "Cloned"];

  const filterOptions = useMemo(
    () => ({
      All: "",
      Public: "&visibility=1",
      Private: "&visibility=2",
      Cloned: "&is_cloned=true",
    }),
    []
  );

  const [selectedTab, setSelectedTab] = useState(mainTabs[0]);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);

  const [selectedWheel, setSelectedWheel] = useState(wheelId || null);
  const [wheelsData, setWheelsData] = useState({
    All: { wheels: [], page: 0, total_pages: 0 },
    Public: { wheels: [], page: 0, total_pages: 0 },
    Private: { wheels: [], page: 0, total_pages: 0 },
    Cloned: { wheels: [], page: 0, total_pages: 0 },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(wheelDropdownOptions[0]);
  const [searchedText, setSearchedText] = useState("");
  const [currentWheelId, setCurrentWheelId] = useState(wheelId || null);
  const [currentWheelData, setCurrentWheelData] = useState(
    selectedWheelData || null
  );

  const userId = getLoggedInUser()?.id;

  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const fetchWheels = useCallback(
    async (filter, currentPage, currentSearchedText) => {
      try {
        setIsLoading(true);
        const query = currentSearchedText
          ? `${filterOptions[filter]}&search=${currentSearchedText}&page=${currentPage}`
          : `${filterOptions[filter]}&page=${currentPage}`;

        // const response = await getUserWheels(userId, query);
        const response = await getQueriedUserWheels(userId, query);
        setWheelsData((prevData) => ({
          ...prevData,
          [filter]: {
            wheels:
              currentPage === 1
                ? response.data.results
                : [...prevData[filter].wheels, ...response.data.results],
            page: currentPage,
            total_pages: response.data.total_pages,
          },
        }));
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.log(err);
      }
    },
    [userId, filterOptions]
  );

  const extractWheelIdFromUrl = (url) => {
    const match = url.match(/wheel\/(\d+)|builder\/(\d+)(\/null)?/);
    return match ? match[1] || match[2] : null;
  };

  const handleEmbedWheel = async (url) => {
    const id = extractWheelIdFromUrl(url);
    if (!id) {
      toast.error("This doesn't look like a wheel URL.", {
        toastId: "link-wheel-invalid-url",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await getWheelDetails(id, "?preview=true");
      if (res?.data?.data) {
        const response = res.data.data;
        setSelectedWheel(response);
        setCurrentWheelData(response);
        setCurrentWheelId(id);
        handleChange("wheelId", id);
      }
    } catch (error) {
      toast.error(error.message, { toastId: `link-wheel-${id}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWheels(selectedFilter, 1, "");
  }, [selectedFilter, fetchWheels]);

  useEffect(() => {
    if (!currentWheelId) return;
    if (String(currentWheelData?.id) === String(currentWheelId)) return;
    getWheelData(currentWheelId);
  }, [currentWheelId]);

  const getWheelData = async (id) => {
    setLoading(true);
    try {
      const res = await getWheelDetails(id, "?preview=true");
      if (res?.data?.data) {
        const response = res.data.data;

        setSelectedWheel(response);
        setCurrentWheelData(response);
      }
    } catch (error) {
      toast.error(error.message, { toastId: `link-wheel-${id}` });
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchWheels = useMemo(
    () =>
      _.debounce((filter, currentPage, currentSearchedText) => {
        fetchWheels(filter, currentPage, currentSearchedText);
      }, 300),
    [fetchWheels]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedFetchWheels.cancel();
    };
  }, [debouncedFetchWheels]);

  const handleFilterChange = (event) => {
    const newFilter = event.target.value;
    setSelectedFilter(newFilter);
    setSearchedText("");
    // Cancel any pending debounced search
    debouncedFetchWheels.cancel();
  };

  const handleLoadMore = () => {
    const nextPage = wheelsData[selectedFilter].page + 1;
    if (nextPage <= wheelsData[selectedFilter].total_pages) {
      fetchWheels(selectedFilter, nextPage, searchedText);
    }
  };

  const handleSearch = (event) => {
    const newValue = event.target.value;
    setSearchedText(newValue);
    debouncedFetchWheels(selectedFilter, 1, newValue);
  };

  const handleSearchIconClick = () => {
    if (searchedText.trim()) {
      fetchWheels(selectedFilter, 1, searchedText);
    }
  };

  const handleChange = (type, value, side) => {
    dispatch(toggleSegmentSettings(false));
    setProp((props) => {
      // Clone the props to avoid mutating Proxies
      const newProps = {
        ...props,
        margin: { ...props.margin },
      };

      switch (type) {
        case "wheelId":
          props.wheelId = value;
          newProps.wheelId = value;
          break;
        case "showDescription":
          props.showDescription = !props.showDescription;
          newProps.showDescription = !props.showDescription;
          break;
        case "borderRadius":
          props.borderRadius = value;
          newProps.borderRadius = value;
          break;
        case "titleColor":
          props.titleColor = value;
          newProps.titleColor = value;
          break;
        case "resetTitleColor":
          props.titleColor = "#123051";
          newProps.titleColor = "#123051";
          break;
        case "textColor":
          props.textColor = value;
          newProps.textColor = value;
          break;
        case "resetTextColor":
          props.textColor = "#565556";
          newProps.textColor = "#565556";
          break;
        case "borderColor":
          props.borderColor = value;
          newProps.borderColor = value;
          break;
        case "resetBorder":
          props.borderColor = "suggested_color_0";
          newProps.borderColor = "suggested_color_0";
          break;
        case "borderWidth":
          props.borderWidth = value;
          newProps.borderWidth = value;
          break;
        case "dropShadowColor":
          props.dropShadowColor = value;
          newProps.dropShadowColor = value;
          break;
        case "resetShadow":
          props.dropShadowColor = null;
          newProps.dropShadowColor = null;
          break;
        case "backgroundColor":
          props.backgroundColor = value;
          newProps.backgroundColor = value;
          break;
        case "resetBackgroundColor":
          props.backgroundColor = "#E5ECFA";
          newProps.backgroundColor = "#E5ECFA";
          break;
        case "backgroundOpacity":
          props.backgroundOpacity = sliderToOpacity(value);
          newProps.backgroundOpacity = sliderToOpacity(value);
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
            newProps.margin[side] = value;
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          newProps.allSideMargin = value;
          break;
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          newProps.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          break;
        default:
          break;
      }

      // Don't include wheelId in the default styles
      const updatedProps = { ...newProps };
      updatedProps.wheelId = ""; // Don't save the specific wheel ID

      // Dispatch the updated props to redux
      dispatch(updateLinkWheelStyles(updatedProps));
    }, 500);
  };

  const handleAdjustment = (type, side, operation) => {
    if (type === "margin") {
      const newValue =
        operation === "increase" ? margin[side] + 1 : margin[side] - 1;
      if (newValue >= 0) handleChange("margin", newValue, side);
    }
  };

  return (
    <div style={{ overflowY: "hidden" }}>
      <Tabs
        categories={mainTabs}
        selectedCategory={selectedTab}
        onCategoryChange={(_, val) => setSelectedTab(val)}
        tabStyle={{ maxWidth: "50%" }}
        disabledCategories={
          selectedWheel || !_.isEmpty(currentWheelData) ? [] : [mainTabs[1]]
        }
      />

      <div style={{ marginBottom: 20 }} />

      {selectedTab === mainTabs[0] && (
        <>
          <TextView variant="subtitle" style={{ marginBottom: 10 }}>
            Linked Wheel
          </TextView>
          {selectedWheel || !_.isEmpty(currentWheelData) ? (
            <WheelInfoCard
              selected
              wheelData={currentWheelData}
              onWheelClick={() => {}}
            />
          ) : (
            <TextView
              variant="text"
              style={{ marginBottom: 10 }}
              color={"#123051"}
            >
              Select a wheel below to link in the segment!
            </TextView>
          )}

          <Tabs
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            tabStyle={{ maxWidth: 120 }}
          />
          {selectedCategory === categories[0] && (
            <BrowseWheels
              handleSearch={handleSearch}
              searchedText={searchedText}
              wheelDropdownOptions={wheelDropdownOptions}
              handleFilterChange={handleFilterChange}
              selectedFilter={selectedFilter}
              wheels={wheelsData[selectedFilter].wheels}
              selectedWheelId={selectedWheel?.id}
              onWheelClick={(data) => {
                setSelectedWheel(data);
                setCurrentWheelId(data.id);
                setCurrentWheelData(data);
                handleChange("wheelId", data?.id);
              }}
              onLoadMore={handleLoadMore}
              hideLoadMore={
                wheelsData[selectedFilter].page + 1 <=
                wheelsData[selectedFilter].total_pages
              }
              isLoading={isLoading}
              onSearchIconClick={handleSearchIconClick}
            />
          )}
          {selectedCategory === categories[1] && (
            <EmbedURL
              type="wheel"
              onConfirm={(value) => handleEmbedWheel(value)}
              isLoading={loading}
            />
          )}
        </>
      )}

      {selectedTab === mainTabs[1] && (
        <>
          <TextView variant="subtitle" style={{ marginBottom: 10 }}>
            Show Description
            <CustomSwitch
              checked={showDescription}
              onChange={() => handleChange("showDescription")}
            />
          </TextView>

          <div style={{ width: "100%", border: "1px solid #f7f2fa" }} />

          <SettingsSlider
            title="Corner Rounding"
            sliderValue={borderRadius}
            onValueChange={(val) => handleChange("borderRadius", val)}
            min={0}
          />

          <MarginBox
            margin={margin}
            increaseMargin={(side) =>
              handleAdjustment("margin", side, "increase")
            }
            decreaseMargin={(side) =>
              handleAdjustment("margin", side, "decrease")
            }
            isApplyMarginOnAllSides={isApplyMarginOnAllSides}
            handleMarginCheckBox={() => handleChange("marginCheckbox")}
            allSideMargin={allSideMargin}
            handleSliderMarginChange={(value) =>
              handleChange("allSideMargin", value, null)
            }
            handleMarginChange={(side, value) =>
              handleChange("margin", value, side)
            }
            maxSliderValue={50}
          />

          <ColorPopup
            title="Title"
            background={titleColor}
            handleChangeBackgroundColor={(val) =>
              handleChange("titleColor", val)
            }
            resetBackgroundColor={() => handleChange("resetTitleColor")}
            showSlider={false}
            showSuggestedColorPalette={true}
          />
          <ColorPopup
            title="Text"
            background={textColor}
            handleChangeBackgroundColor={(val) =>
              handleChange("textColor", val)
            }
            resetBackgroundColor={() => handleChange("resetTextColor")}
            showSlider={false}
            showSuggestedColorPalette={true}
          />
          <ColorPopup
            title="Background"
            background={backgroundColor}
            handleChangeBackgroundColor={(val) =>
              handleChange("backgroundColor", val)
            }
            resetBackgroundColor={() => handleChange("resetBackgroundColor")}
            sliderValue={opacityToSlider(backgroundOpacity)}
            sliderChange={(val) => handleChange("backgroundOpacity", val)}
            sliderBoxTitle={"Opacity"}
            showSuggestedColorPalette={true}
          />
          <ColorPopup
            title="Border Color"
            background={borderColor}
            handleChangeBackgroundColor={(val) =>
              handleChange("borderColor", val)
            }
            resetBackgroundColor={() => handleChange("resetBorder")}
            sliderBoxTitle={"Border Width"}
            sliderValue={borderWidth}
            sliderChange={(val) => handleChange("borderWidth", val)}
            sliderMinValue={0}
            sliderMaxValue={8}
            sliderSymbol={"px"}
            isHideGradient={true}
            showSuggestedColorPalette={true}
            isBorderSlider
          />
          <ColorPopup
            title="Drop Shadow"
            background={dropShadowColor}
            handleChangeBackgroundColor={(val) =>
              handleChange("dropShadowColor", val)
            }
            resetBackgroundColor={() => handleChange("resetShadow")}
            showSlider={false}
            hideDivider
            showSuggestedColorPalette={true}
          />
        </>
      )}
    </div>
  );
};

export default LinkWheelSettings;
